import {Filters} from "@/components/layouts/page/TableLayout/Filters";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import bar_icon from "@images/table/bar-icon.svg";
import {
  getMarkersScore,
  getSearchType,
  setMarkersScoreDate,
  setMarkersScoreSearchType,
} from "@redux/slices/dashboardSlices/dashboardCertificationSlice";
import {WarningData} from "@ui/general/WarningData";
import {
  Chart as ChartJS,
  Filler,
  PointElement,
  RadarController,
  RadialLinearScale,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment";
import {Chart} from "react-chartjs-2";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {ChartLabel} from "../../components/ChartLabel";
import {Head} from "../../components/Head";
import styles from "./styles.module.scss";

ChartJS.register(
  Title,
  Filler,
  PointElement,
  Tooltip,
  RadarController,
  RadialLinearScale,
);

export const MarkersScore = () => {
  const minDate = moment().subtract(10, "years").toDate();
  const [withArchived, setWithArchived] = useState(false);
  const searchRequestsRef = useRef({});
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {width, isMobile} = useSelector((state) => state.common.settings);

  const chartFontSize = (width * 0.75) / 100;
  const chartPadding = (width * 0.5) / 100;
  const chartBorder = (width * 0.25) / 100;
  const margin = (width * 0.5) / 100;

  const {date: markersScoreDate, data: markersScoreData} = useSelector(
    (state) => state.dashboard.certification.markersScore,
  );

  const searchType = useSelector(
    (state) => state.dashboard.certification.markersScore.searchType,
  );
  const selectedIds = useSelector(
    (state) =>
      state.dashboard.certification.markersScore.searchType.selectedIds,
  );

  const onSetDate = async (key, date) => {
    const isoValue = date instanceof Date ? date.toISOString() : date;
    const newDate = {...markersScoreDate, [key]: isoValue};

    await dispatch(setMarkersScoreDate(newDate));
  };

  const runSearch = useCallback(
    (type, name = "") => {
      if (searchRequestsRef.current[type]?.abort) {
        searchRequestsRef.current[type].abort();
      }
      const request = dispatch(
        getSearchType({
          type,
          name,
          section: "markersScore",
          archived: withArchived,
        }),
      );
      searchRequestsRef.current[type] = request;
      return request;
    },
    [dispatch, withArchived],
  );

  const handleSearch = useCallback(
    (type, searchValue) => {
      runSearch(type, searchValue?.trim() || "");
    },
    [runSearch],
  );

  useEffect(() => {
    runSearch("certification");
    runSearch("manager");
    runSearch("representative");
    runSearch("product_line");

    return () => {
      Object.values(searchRequestsRef.current).forEach((request) => {
        if (request?.abort) request.abort();
      });
    };
  }, [runSearch]);

  const filtersList = useMemo(
    () => [
      {
        id: "certification",
        placeholder: t("page.dashboard.certification_placeholder"),
        searchPlaceholder: t("page.dashboard.certification_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.certification?.find(
            (opt) => opt.id === selectedIds?.certification,
          )?.name || "",
        options: searchType.certification,
        onSelect: (option) => {
          dispatch(
            setMarkersScoreSearchType({
              type: "certification",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("certification", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
      {
        id: "manager",
        placeholder: t("page.dashboard.manager_placeholder"),
        searchPlaceholder: t("page.dashboard.manager_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.manager?.find((opt) => opt.id === selectedIds?.manager)
            ?.name || "",
        options: searchType.manager || [],
        onSelect: (option) => {
          dispatch(
            setMarkersScoreSearchType({
              type: "manager",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("manager", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
      {
        id: "representative",
        placeholder: t("page.dashboard.representative_placeholder"),
        searchPlaceholder: t("page.dashboard.representative_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.representative?.find(
            (opt) => opt.id === selectedIds?.representative,
          )?.name || "",
        options: searchType.representative || [],
        onSelect: (option) => {
          dispatch(
            setMarkersScoreSearchType({
              type: "representative",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("representative", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
      {
        id: "product_line",
        placeholder: t("page.dashboard.product_line_placeholder"),
        searchPlaceholder: t("page.dashboard.product_line_search"),
        resetOptionLabel: t("input.not_selected"),
        activeOption:
          searchType.product_line?.find(
            (opt) => opt.id === selectedIds?.product_line,
          )?.name || "",
        options: searchType.product_line || [],
        onSelect: (option) => {
          dispatch(
            setMarkersScoreSearchType({
              type: "product_line",
              id: option === null ? null : option.id,
            }),
          );
        },
        onSearch: (searchValue) => handleSearch("product_line", searchValue),
        emptyResult: null,
        multiSelect: false,
      },
    ],
    [t, searchType, selectedIds, dispatch, handleSearch],
  );

  const markersChartLabelsList = [
    {id: 0, name: t("page.dashboard.average_score_markers"), icon: bar_icon},
  ];

  const markersChartData = useMemo(() => {
    let labels = [];
    let dataValues = [];

    if (
      markersScoreData &&
      typeof markersScoreData === "object" &&
      !Array.isArray(markersScoreData)
    ) {
      const keys = Object.keys(markersScoreData);

      if (keys.length > 0) {
        labels = keys.map((key) => {
          return key.replace(/^\d+\.?\s*/, "").trim();
        });

        dataValues = keys.map((key) => {
          const value = markersScoreData[key];
          return typeof value === "number" ? value : 0;
        });
      }
    }

    if (labels.length === 0) {
      labels = [];
      dataValues = [];
    }

    return {
      labels,
      datasets: [
        {
          label: t("page.dashboard.average_score_markers"),
          backgroundColor: "rgba(138, 106, 203, 0.5)",
          hoverBackgroundColor: "rgba(138, 106, 203)",
          borderColor: "rgb(138, 106, 203)",
          borderRadius: margin,
          borderWidth: 2,
          barPercentage: isMobile ? 1 : 0.5,
          data: dataValues,
          fill: true,
          pointBorderColor: "rgb(138, 106, 203)",
          pointBackgroundColor: "rgb(138, 106, 203)",
          pointHoverBorderColor: "rgb(138, 106, 203)",
        },
      ],
    };
  }, [markersScoreData, margin, isMobile, t]);

  const markersChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 3000,
    },
    scales: {
      r: {
        min: 0,
        beginAtZero: true,
      },
    },
    elements: {
      line: {
        borderWidth: width >= 1920 || isMobile ? 5 : chartBorder,
        capBezierPoints: false,
      },
      point: {
        radius: width >= 1920 || isMobile ? 5 : chartBorder,
        hoverRadius: width >= 1920 || isMobile ? 6 : chartBorder + 0.5,
        borderWidth: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#414040",
        bodyColor: "#414040",
        usePointStyles: true,
        borderColor: "rgba(247, 207, 221, 0.80)",
        padding: width >= 1920 || isMobile ? 20 : chartPadding,
        caretPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        cornerRadius: width >= 1920 || isMobile ? 20 : chartPadding,
        boxPadding: width >= 1920 || isMobile ? 20 : chartPadding,
        usePointStyle: true,
        borderWidth: width >= 1920 || isMobile ? 2 : chartBorder - 1.5,
        titleAlign: "center",
        bodyAlign: "right",
        titleFont: {
          size: width >= 1920 || isMobile ? 16 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
        bodyFont: {
          size: width >= 1920 || isMobile ? 14 : chartFontSize,
          family: "'IBM Plex Sans', sans-serif",
        },
      },
    },
  };

  useEffect(() => {
    dispatch(
      getMarkersScore({
        scenario_id: selectedIds?.certification,
        manager_id: selectedIds?.manager,
        representative_id: selectedIds?.representative,
        product_line_id: selectedIds?.product_line,
        start_date: markersScoreDate.from,
        finish_date: markersScoreDate.to,
        withArchived,
      }),
    );
  }, [
    markersScoreDate.from,
    markersScoreDate.to,
    withArchived,
    selectedIds?.certification,
    selectedIds?.manager,
    selectedIds?.representative,
    selectedIds?.product_line,
    dispatch,
  ]);

  return (
    <div className={styles.markersScore}>
      <Head
        title={t("page.dashboard.markers_score_title")}
        checkboxLabel={t("page.dashboard.checkbox_filter")}
        setWithArchived={setWithArchived}
        withArchived={withArchived}
        chartsDate={markersScoreDate}
        minDate={minDate}
        setFromDate={(date) => onSetDate("from", date)}
        setToDate={(date) => onSetDate("to", date)}
        maxWidth={"100%"}
      />

      <Filters filtersList={filtersList} />

      <div className={styles.markersScoreChartsWrapper}>
        <div className={styles.markersScoreChart}>
          {markersChartData.labels.length > 0 ? (
            <Chart
              type={"radar"}
              options={markersChartOptions}
              data={markersChartData}
            />
          ) : (
            <WarningData type={"empty"} />
          )}
        </div>

        <ChartLabel labelsList={markersChartLabelsList} />
      </div>
    </div>
  );
};
